"use client"

import React from "react"
import { useRouter } from "next/router"

import { AgentBreadcrumbContent } from "@/agents/navigation"
import { SofiaHomeThreadSwitcher } from "@/app-covi"
import { HeaderActionControls } from "@/app-shell/navigation/HeaderActionControls"
import { SIDEBAR_COLLAPSE_BREAKPOINT } from "@/app-shell/navigation/sidebarDimensions"
import { SOFIA_PRODUCT_NAME } from "@/constants/sofia"
import { useCovalOnly } from "@/feature-flags"
import { MetricBreadcrumbContent } from "@/metrics/navigation"
import { ReportBreadcrumbContent } from "@/reports/navigation"
import {
  RunBreadcrumbContent,
  RunBreadcrumbContentWithCopy,
} from "@/runs/navigation"
import { ScheduledRunBreadcrumbContent } from "@/runs/scheduled"
import { TemplateBreadcrumbContent } from "@/templates/navigation"
import {
  TestSetBreadcrumbActions,
  TestSetBreadcrumbContent,
} from "@/test-set/editor"
import { useOrganization } from "@clerk/nextjs"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Tooltip,
} from "@coval/ui"
import { Check, Copy } from "lucide-react"
import { useMediaQuery } from "usehooks-ts"

import { useHeaderActions } from "@/contexts/HeaderActionsContext"
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard"

import {
  buildRichBreadcrumbDescriptors,
  type RichBreadcrumbDescriptor,
} from "./richDefaultPageHeaderBreadcrumbs"

interface RichDefaultPageHeaderProps {
  getHref: (baseHref: string) => string
  isDashboardPage: boolean
  dashboardDisplayName?: string
  dashboardHeaderActions?: React.ReactNode
}

export const RichDefaultPageHeader = ({
  getHref,
  isDashboardPage,
  dashboardDisplayName,
  dashboardHeaderActions,
}: RichDefaultPageHeaderProps) => {
  const router = useRouter()
  const { organization } = useOrganization()
  const { headerAction } = useHeaderActions()
  // Only promise restore when Recently Deleted is actually reachable (Coval-only).
  const recentlyDeletedEnabled = useCovalOnly()
  // Below this width the sidebar collapses to icons and cannot carry Sofia's
  // own rows, so the breadcrumb takes the thread history instead.
  const isCompactSidebar = useMediaQuery(
    `(max-width: ${SIDEBAR_COLLAPSE_BREAKPOINT}px)`,
  )

  const renderLinkOrPage = (descriptor: {
    href?: string
    children: React.ReactNode
  }) => {
    if (!descriptor.href) {
      return <BreadcrumbPage>{descriptor.children}</BreadcrumbPage>
    }

    return (
      <BreadcrumbLink href={descriptor.href}>
        {descriptor.children}
      </BreadcrumbLink>
    )
  }

  const renderBreadcrumbDescriptor = (
    descriptor: RichBreadcrumbDescriptor,
  ): React.ReactNode => {
    switch (descriptor.type) {
      case "label":
        return renderLinkOrPage({
          href: descriptor.href,
          children: descriptor.label,
        })
      case "sofia-thread-switcher":
        return <SofiaHomeThreadSwitcher />
      case "sofia-brand":
        return (
          <BreadcrumbPage className="font-semibold text-coval-text">
            {SOFIA_PRODUCT_NAME}
          </BreadcrumbPage>
        )
      case "run-title":
        return renderLinkOrPage({
          href: descriptor.href,
          children: descriptor.withCopy ? (
            <RunBreadcrumbContentWithCopy runId={descriptor.runId} />
          ) : (
            <RunBreadcrumbContent runId={descriptor.runId} />
          ),
        })
      case "copyable-id":
        return (
          <BreadcrumbPage>
            <CopyableIdBreadcrumb id={descriptor.id} />
          </BreadcrumbPage>
        )
      case "test-set":
        return renderLinkOrPage({
          href: descriptor.href,
          children: (
            <TestSetBreadcrumbContent
              segment={descriptor.id}
              isLast={descriptor.isLast}
            />
          ),
        })
      case "agent":
        return renderLinkOrPage({
          href: descriptor.href,
          children: (
            <AgentBreadcrumbContent
              agentId={descriptor.id}
              isLast={descriptor.isLast}
            />
          ),
        })
      case "metric":
        return renderLinkOrPage({
          href: descriptor.href,
          children: (
            <MetricBreadcrumbContent
              metricId={descriptor.id}
              isLast={descriptor.isLast}
            />
          ),
        })
      case "report":
        return renderLinkOrPage({
          href: descriptor.href,
          children: (
            <ReportBreadcrumbContent
              reportId={descriptor.id}
              isLast={descriptor.isLast}
            />
          ),
        })
      case "template":
        return renderLinkOrPage({
          href: descriptor.href,
          children: (
            <TemplateBreadcrumbContent
              templateId={descriptor.id}
              isLast={descriptor.isLast}
            />
          ),
        })
      case "scheduled-run":
        return renderLinkOrPage({
          href: descriptor.href,
          children: (
            <ScheduledRunBreadcrumbContent
              scheduledRunId={descriptor.id}
              isLast={descriptor.isLast}
            />
          ),
        })
    }
  }

  // Sofia Home's chat switcher is the fallback form of the sidebar's own Sofia
  // rows: the sidebar carries the history where it has the width, this crumb
  // where it does not. Filtering the descriptor out here rather than inside the
  // switcher keeps the separator chevrons counted off what actually renders.
  const breadcrumbDescriptors = buildRichBreadcrumbDescriptors({
    pathname: router.asPath,
    organizationSlug: organization?.slug ?? undefined,
    isDashboardPage,
    dashboardDisplayName,
    getHref,
  }).filter(
    (descriptor) =>
      descriptor.type !== "sofia-thread-switcher" || isCompactSidebar,
  )

  return (
    <div className="flex flex-1 min-w-0 items-center justify-between">
      <div className="flex justify-start gap-2 items-center flex-1 min-w-0">
        <Breadcrumb>
          <BreadcrumbList>
            {breadcrumbDescriptors.map((descriptor, i) => (
              <React.Fragment key={descriptor.key}>
                {i > 0 && <BreadcrumbSeparator />}
                <BreadcrumbItem>
                  {renderBreadcrumbDescriptor(descriptor)}
                </BreadcrumbItem>
              </React.Fragment>
            ))}
          </BreadcrumbList>
        </Breadcrumb>
      </div>
      <div className="flex min-w-0 items-center gap-2">
        {isDashboardPage && dashboardHeaderActions}
        <HeaderActionControls headerAction={headerAction} />
        <TestSetBreadcrumbActions
          recentlyDeletedEnabled={recentlyDeletedEnabled}
        />
      </div>
    </div>
  )
}

function CopyableIdBreadcrumb({ id }: { id: string }) {
  const { copied, copy } = useCopyToClipboard()

  return (
    <span className="flex items-center gap-1">
      <span>{id}</span>
      <Tooltip content={copied ? "Copied!" : "Copy ID"}>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            void copy(id)
          }}
          className="p-0.5 inline-flex items-center justify-center bg-transparent border-0 cursor-pointer focus:outline-none"
          aria-label={copied ? "Copied!" : "Copy ID"}
        >
          {copied ? (
            <Check className="h-3 w-3 text-coval-success-text" />
          ) : (
            <Copy className="h-3 w-3" />
          )}
        </button>
      </Tooltip>
    </span>
  )
}
